import React from 'react';
import Box from '../../components/Common/Box';

const Toggle = ({ checked, onChange, disabled, label }: { checked: boolean, onChange: (v: boolean) => void, disabled?: boolean, label: string }) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0' }}>
        <span style={{ color: 'var(--text-main)', fontWeight: 500, fontSize: '14px' }}>{label}</span>
        <div 
            onClick={() => !disabled && onChange(!checked)}
            style={{ 
                width: '48px', 
                height: '26px', 
                backgroundColor: checked ? '#48bb78' : '#cbd5e0', 
                borderRadius: '13px', 
                position: 'relative', 
                cursor: disabled ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                opacity: disabled ? 0.6 : 1
            }}
        >
            <div style={{ 
                width: '20px', 
                height: '20px', 
                backgroundColor: 'white', 
                borderRadius: '50%', 
                position: 'absolute', 
                top: '3px', 
                left: checked ? '25px' : '3px',
                transition: 'all 0.3s ease',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
            }} />
        </div>
    </div>
);

interface MobileProfileProps {
    user: any;
    theme: string;
    isEditing: boolean;
    setIsEditing: (v: boolean) => void;
    fullName: string;
    setFullName: (v: string) => void;
    pseudonym: string;
    setPseudonym: (v: string) => void;
    isOptIn: boolean;
    setIsOptIn: (v: boolean) => void;
    showRealName: boolean;
    setShowRealName: (v: boolean) => void;
    loading: boolean;
    successMsg: string;
    errorMsg: string;
    showConfirmReset: boolean;
    handleToggleTheme: () => void;
    handleSaveProfile: () => void;
    handleResetPassword: () => void;
    handleUpdateSocialSettings: (optIn: boolean, ps: string, sr?: boolean) => void;
    setShowConfirmReset: (v: boolean) => void;
    logout: () => void;
    getInitials: (name: string) => string;
    getPlanName: (roleName?: string) => string;
    formatDate: (dateString?: string) => string;
}

const MobileProfile: React.FC<MobileProfileProps> = ({
    user, theme, isEditing, setIsEditing, fullName, setFullName,
    pseudonym, setPseudonym, isOptIn, setIsOptIn, showRealName, setShowRealName, loading,
    successMsg, errorMsg, showConfirmReset,
    handleToggleTheme, handleSaveProfile, handleResetPassword, handleUpdateSocialSettings,
    setShowConfirmReset, logout, getInitials, getPlanName, formatDate
}) => {

    const inputStyle: React.CSSProperties = {
        width: '100%', padding: '14px', borderRadius: '10px',
        border: '1px solid var(--border-color)', backgroundColor: 'var(--bg-input)',
        fontSize: '15px', color: 'var(--text-heading)', boxSizing: 'border-box',
    };

    const readonlyFieldStyle: React.CSSProperties = {
        padding: '14px', backgroundColor: 'var(--bg-input)', borderRadius: '10px',
        color: 'var(--text-heading)', fontWeight: 500, fontSize: '15px',
    };

    const labelStyle: React.CSSProperties = {
        display: 'block', marginBottom: '6px', color: 'var(--text-muted)', fontSize: '13px', fontWeight: '600',
    };

    const fullWidthBtnStyle: React.CSSProperties = {
        width: '100%', padding: '14px', borderRadius: '10px', fontSize: '15px',
        fontWeight: '600', cursor: 'pointer', border: 'none', minHeight: '48px',
        touchAction: 'manipulation',
    };

    return (
        <div style={{ paddingBottom: '30px', background: 'var(--bg-main)' }}>

            {/* Notifications */}
            {successMsg && (
                <div style={{ margin: '12px 16px', padding: '12px 16px', backgroundColor: '#f0fff4', color: '#2f855a', borderRadius: '10px', border: '1px solid #c6f6d5', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    ✅ {successMsg}
                </div>
            )}
            {errorMsg && (
                <div style={{ margin: '12px 16px', padding: '12px 16px', backgroundColor: '#fff5f5', color: '#c53030', borderRadius: '10px', border: '1px solid #fed7d7', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    ❌ {errorMsg}
                </div>
            )}

            {/* Hero Avatar Section */}
            <div style={{ padding: '30px 20px 24px', background: 'var(--bg-box)', borderBottom: '1px solid var(--border-color)', textAlign: 'center' }}>
                <div style={{ position: 'relative', display: 'inline-block', marginBottom: '16px' }}>
                    <div style={{
                        width: '88px', height: '88px', borderRadius: '50%', backgroundColor: '#667eea',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '32px', color: 'white', fontWeight: 'bold',
                        border: '3px solid white', boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
                    }}>
                        {user?.full_name ? getInitials(user.full_name) : 'U'}
                    </div>
                    <button style={{
                        position: 'absolute', bottom: '-2px', right: '-2px',
                        backgroundColor: '#fff', border: '1px solid #ddd', borderRadius: '50%',
                        width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: 'pointer', boxShadow: '0 2px 5px rgba(0,0,0,0.1)', fontSize: '14px',
                    }} title="Changer la photo">📷</button>
                </div>

                <h2 style={{ margin: '0 0 4px 0', fontSize: '20px', color: 'var(--text-heading)' }}>
                    {user?.full_name || 'Utilisateur'}
                </h2>
                <p style={{ margin: '0 0 12px 0', color: 'var(--text-muted)', fontSize: '14px' }}>{user?.email || 'email@example.com'}</p>

                <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <span style={{ backgroundColor: user?.is_active ? '#e6fffa' : '#fff5f5', color: user?.is_active ? '#38b2ac' : '#e53e3e', padding: '4px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: 'bold' }}>
                        {user?.is_active ? 'Actif' : 'Inactif'}
                    </span>
                    <span style={{ backgroundColor: user?.is_email_verified ? '#ebf8ff' : '#fff5f5', color: user?.is_email_verified ? '#3182ce' : '#e53e3e', padding: '4px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: 'bold' }}>
                        {user?.is_email_verified ? 'Email Vérifié ✓' : 'Non Vérifié ⚠️'}
                    </span>
                </div>
            </div>

            {/* Profile Details - Stacked */}
            <div style={{ padding: '20px 16px' }}>
                <Box>
                    <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        {/* Full Name */}
                        <div>
                            <label style={labelStyle}>Nom Complet</label>
                            {isEditing ? (
                                <input type="text" value={fullName} onChange={(e) => setFullName(e.target.value)} style={inputStyle} />
                            ) : (
                                <div style={readonlyFieldStyle}>{user?.full_name || '—'}</div>
                            )}
                        </div>

                        {/* Email */}
                        <div>
                            <label style={labelStyle}>Email</label>
                            <div style={readonlyFieldStyle}>{user?.email || '—'}</div>
                        </div>

                        {/* Plan */}
                        <div>
                            <label style={labelStyle}>Abonnement</label>
                            <div style={{ ...readonlyFieldStyle, backgroundColor: 'rgba(49, 130, 206, 0.1)', color: '#63b3ed', fontWeight: 'bold', border: '1px solid rgba(49, 130, 206, 0.2)' }}>
                                {getPlanName(user?.role?.name)}
                            </div>
                        </div>

                        {/* Expiration */}
                        <div>
                            <label style={labelStyle}>Expiration</label>
                            <div style={readonlyFieldStyle}>
                                {user?.role?.name === 'global_admin' ? 'À vie (Admin)' : formatDate(user?.subscription_end_date)}
                            </div>
                        </div>

                        {/* Organisation */}
                        {user?.tenant?.name && (
                            <div>
                                <label style={labelStyle}>Organisation (B2B)</label>
                                <div style={{ ...readonlyFieldStyle, backgroundColor: 'rgba(56, 161, 105, 0.1)', color: '#4ade80', border: '1px solid rgba(56, 161, 105, 0.2)' }}>
                                    {user.tenant.name} {user.tenant.plan ? `(${user.tenant.plan})` : ''}
                                </div>
                            </div>
                        )}
                    </div>
                </Box>

                {/* Social & Competition */}
                <div style={{ marginTop: '16px' }}>
                    <Box>
                        <div style={{ padding: '16px' }}>
                            <h3 style={{ margin: '0 0 14px 0', fontSize: '15px', color: 'var(--text-heading)' }}>Communauté & Privacy</h3>

                            <Toggle 
                                label="Je ne souhaite pas participer au Leaderboard public"
                                checked={!isOptIn}
                                disabled={loading}
                                onChange={(val: boolean) => {
                                    const newOptIn = !val;
                                    setIsOptIn(newOptIn);
                                    handleUpdateSocialSettings(newOptIn, pseudonym);
                                }}
                            />

                            <Toggle 
                                label="Afficher mon vrai nom (Forum)"
                                checked={showRealName}
                                disabled={loading}
                                onChange={(val: boolean) => {
                                    setShowRealName(val);
                                    handleUpdateSocialSettings(isOptIn, pseudonym, val);
                                }}
                            />
                            <p style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '-8px', marginBottom: '15px' }}>
                                {showRealName ? "Nom complet visible sur le forum." : "Pseudonyme utilisé par défaut."}
                            </p>

                            {isOptIn && (
                                <div style={{ marginTop: '12px', borderTop: '1px solid var(--border-color)', paddingTop: '15px' }}>
                                    <label style={labelStyle}>Pseudonyme</label>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                        <input
                                            type="text"
                                            value={pseudonym}
                                            onChange={(e) => setPseudonym(e.target.value)}
                                            placeholder="Ex: LionDuPlateau"
                                            disabled={loading}
                                            style={inputStyle}
                                        />
                                        {pseudonym !== (user?.social_profile?.pseudonym || '') && (
                                            <button
                                                onClick={() => handleUpdateSocialSettings(isOptIn, pseudonym)}
                                                disabled={loading || !pseudonym}
                                                style={{ ...fullWidthBtnStyle, background: '#48bb78', color: 'white' }}
                                            >
                                                Mettre à jour le pseudonyme
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </Box>
                </div>

                {/* Display Preferences */}
                <div style={{ marginTop: '16px' }}>
                    <Box>
                        <div style={{ padding: '16px' }}>
                            <h3 style={{ margin: '0 0 14px 0', fontSize: '15px', color: 'var(--text-heading)' }}>Préférences</h3>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div>
                                    <strong style={{ color: 'var(--text-main)', fontSize: '14px' }}>Thème</strong>
                                    <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: 'var(--text-muted)' }}>Mode {theme === 'dark' ? 'sombre' : 'clair'} activé</p>
                                </div>
                                <button onClick={handleToggleTheme} style={{
                                    display: 'flex', alignItems: 'center', gap: '6px',
                                    padding: '10px 16px', borderRadius: '20px',
                                    backgroundColor: theme === 'dark' ? 'var(--icon-bg)' : '#ffffff',
                                    color: 'var(--text-heading)', border: '1px solid var(--border-color)',
                                    cursor: 'pointer', fontWeight: 600, fontSize: '13px',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)', transition: 'all 0.3s ease',
                                    minHeight: '44px',
                                }}>
                                    {theme === 'dark' ? '☀️ Clair' : '🌙 Sombre'}
                                </button>
                            </div>
                        </div>
                    </Box>
                </div>

                {/* Actions - Stacked Full Width */}
                <div style={{ marginTop: '24px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {isEditing ? (
                        <button disabled={loading} onClick={handleSaveProfile} style={{ ...fullWidthBtnStyle, background: '#48bb78', color: 'white', boxShadow: '0 4px 6px rgba(72, 187, 120, 0.25)' }}>
                            {loading ? 'Enregistrement...' : '✅ Enregistrer les modifications'}
                        </button>
                    ) : (
                        <button onClick={() => setIsEditing(true)} style={{ ...fullWidthBtnStyle, background: '#667eea', color: 'white', boxShadow: '0 4px 6px rgba(102, 126, 234, 0.25)' }}>
                            ✏️ Modifier le profil
                        </button>
                    )}

                    <button disabled={loading} onClick={handleResetPassword} style={{
                        ...fullWidthBtnStyle,
                        background: showConfirmReset ? '#fff5f5' : 'var(--bg-box)',
                        color: showConfirmReset ? '#c53030' : 'var(--text-main)',
                        border: `1px solid ${showConfirmReset ? '#feb2b2' : 'var(--border-color)'}`,
                        opacity: loading ? 0.7 : 1,
                    }}>
                        {loading ? 'Envoi...' : (showConfirmReset ? "⚠️ Confirmer l'envoi" : '🔑 Modifier le mot de passe')}
                    </button>

                    {showConfirmReset && (
                        <button onClick={() => setShowConfirmReset(false)} style={{ ...fullWidthBtnStyle, background: 'transparent', color: 'var(--text-muted)', border: '1px solid var(--border-color)' }}>
                            Annuler
                        </button>
                    )}

                    <button onClick={logout} style={{ ...fullWidthBtnStyle, background: 'rgba(229, 62, 62, 0.1)', color: '#f87171', border: '1px solid rgba(229, 62, 62, 0.2)', marginTop: '8px' }}>
                        🚪 Déconnexion
                    </button>
                </div>
            </div>
        </div>
    );
};

export default MobileProfile;
